<template>
    <div class="sm:flex flex-wrap lg:gap-x-12 justify-between">
        <div class="sm:w-1/2 md:w-1/3 xl:w-1/4">
            <div class="flex items-center mb-2">
                <jet-label for="id" value="Order #:" class="flex w-32 mb-0 leading-7"/>
                <Link v-if="isCustomer" class="block mb-1" :href="route('customer_orders.show', order.id)">
                    {{ order.order_number }}
                </Link>
                <Link v-else class="block mb-1" :href="route('orders.show', order.id)">
                    {{ order.order_number }}
                </Link>
            </div>

            <div class="flex items-center mb-2">
                <jet-label for="ordered_date" value="Ordered Date:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex items-end whitespace-nowrap mb-1">
                    {{ formatDate(order.order_date) }}&nbsp;&nbsp;
                    <span v-if="!order.is_wholesale" class="text-gray-400 text-sm">
                        {{ formatTime(order.created_at) }}
                    </span>
                </div>
            </div>

            <div class="flex items-center mb-2">
                <jet-label for="delivery_date" value="Delivery Date:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex items-end whitespace-nowrap mb-1">
                    {{ formatDate(order.ship_date) }}&nbsp;&nbsp;
                    <span v-if="!order.is_wholesale" class="text-gray-400 text-sm">
                        {{ order.shipping_method === 'pickup' ? order.pickup_time : order.shipping_time }}
                    </span>
                </div>
            </div>

            <div class="flex items-center">
                <jet-label for="status" value="Status:" class="flex w-32 mb-0 leading-7 self-start" />
                <div class="flex flex-col mb-1">
                    <div>{{ order.statusDef }}</div>
                    <div v-if="order.invoice_emailed_at" class="text-xs italic">
                        Emailed on {{ formatTimestamp(order.invoice_emailed_at) }}
                    </div>
                </div>
            </div>
            
            <div class="flex items-center mb-2">
                <jet-label for="id" value="Paid Amount:" class="flex w-32 mb-0 leading-7"/>
                <div class="flex mb-1">
                    {{ toCurrency(order.charged_total) }}
                </div>
            </div>
        </div>

        <div class="sm:w-1/2 md:w-1/3 xl:w-1/4">
            <div class="flex items-center mb-2">
                <div class="flex items-center mb-2">
                    <jet-label for="billed_to" value="Customer:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7 self-start" />
                    <div class="flex mb-1">
                        {{ order.customer?.name || order.shipping_first_name + ' ' + order.shipping_last_name }}
                    </div>
                </div>
            </div>
            <div class="flex items-start mb-2">
                <jet-label for="address" value="Address:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7" />
                <div class="leading-tight mb-1">
                    <template v-if="order.customer">
                        <div>{{ order.customer.address_1 }}</div>
                        <div v-show="order.customer.address_2">{{ order.customer.address_2 }}</div>
                        <div>{{ order.customer.city }} {{ order.customer.province }}, {{ order.customer.postal_code }}</div>
                    </template>

                    <template v-else>
                        <div>{{ order.shipping_address_1 }}</div>
                        <div v-show="order.shipping_address_2">{{ order.shipping_address_2 }}</div>
                        <div>{{ order.shipping_city }} {{ order.shipping_province }}, {{ order.shipping_postal }}</div>
                    </template>
                </div>
            </div>
            <div class="flex items-center mb-2">
                <jet-label for="phone" value="Phone:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7" />
                <div class="flex mb-1">
                    {{ order.customer?.phone || order.shipping_phone }}
                </div>
            </div>

            <div class="flex items-start mb-2" v-if="order.customer?.emails.length || order.shipping_email">
                <jet-label for="email" value="Email:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7" />
                <div class="flex flex-1 mb-1">
                    <ul v-if="order.customer" class="leading-tight">
                        <li v-for="email in order.customer.emails" :key="email" class="break-all">{{ email }}</li>
                    </ul>
                    <template v-else>
                        {{ order.shipping_email }}
                    </template>
                </div>
            </div>
        </div>

        <div class="sm:w-1/2 md:w-1/3">
            <span v-if="!isCreate">
                <div class="flex items-center mb-2">
                    <jet-label for="id" value="Refund Amount:" class="flex w-32 mb-0 leading-7"/>
                    <div class="flex mb-1">
                        {{ toCurrency(parseFloat(refund.amount)) }}
                    </div>
                </div>
    
                <div class="flex items-center mb-2">
                    <jet-label for="id" value="Refund Status:" class="flex w-32 mb-0 leading-7"/>
                    <div class="flex mb-1">
                        {{ refund.statusDef }}
                    </div>
                </div>
    
                <div v-if="!isCustomer && refund.reviewed_by" class="flex items-center mb-2">
                    <jet-label for="id" value="Reviewed By:" class="flex w-32 mb-0 leading-7"/>
                    <div class="flex mb-1">
                        {{ refund.reviewed_by?.name }}
                    </div>
                </div>

                <div v-if="!isCustomer && refund.reviewed_date" class="flex items-center mb-2">
                    <jet-label for="id" value="Reviewed At:" class="flex w-32 mb-0 leading-7"/>
                    <div class="flex mb-1">
                        {{ formatTimestamp(refund.reviewed_date) }}, {{ formatTime(refund.reviewed_date) }}
                    </div>
                </div>

                <div v-if="refund.status === 'refunded'" class="flex items-center mb-2">
                    <jet-label  value="Refund Method:" class="flex w-32 mb-0 leading-7"/>
                    <div class="flex mb-1">
                        {{ refund.refund_method }}
                    </div>
                </div>
    
                <div v-if="refund.reviewed_note" class="flex items-center mb-2">
                    <jet-label for="id" value="Review Notes:" class="flex w-32 mb-0 leading-7"/>
                    <div class="flex mb-1">
                        {{ refund.reviewed_note }}
                    </div>
                </div>

                <div v-if="refund.refund_order" class="flex items-center mb-2">
                    <jet-label for="id" value="Credit Order #:" class="flex w-32 mb-0 leading-7"/>
                    <div class="flex mb-1">
                        <Link v-if="isCustomer" :href="route('customer_orders.show', refund.refund_order.id)">{{ refund.refund_order.id }}</Link>
                        <Link v-else :href="route('orders.show', refund.refund_order.id)">{{ refund.refund_order.id }}</Link>
                    </div>
                </div>

                <div v-if="refund?.promo_code" class="flex items-center mb-2">
                    <jet-label for="promo_code_id" value="Promo Code:" class="flex w-32 mb-0 leading-7"/>
                    <div class="flex mb-1">
                        {{ refund.promo_code?.code }}
                    </div>
                </div>
            </span>
        </div>
    </div>
</template>

<script>
import { usePage, Link } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import { DateTime } from "luxon";

export default {
    setup () {
        const { isCustomer } = UseUserAuth(usePage().props.value.user);

        return { isCustomer }
    },

    components: {
        JetInput,
        JetLabel,
        Link,
    },

    props: [
        'order',
        'isCreate',
        'refund',
    ],

    methods: {
        formatDate (date) {
            if (!date) {
                return '-';
            }

            return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        },

        formatTime(timestamp) {
            let date = DateTime.fromSQL(timestamp).invalid ? DateTime.fromISO(timestamp) : DateTime.fromSQL(timestamp);

            if (date.c.hour === 0 && date.c.minute === 0 && date.c.second === 0) {
                return '';
            }

            return date.toFormat('h:mm a');
        },

        formatTimestamp (timestamp) {
            return DateTime.fromSQL(timestamp).toFormat('MMM d, yyyy');
        },
    }
}
</script>

<style>
.dp-custom-input {
    @apply h-8;
}
.box>* {
    flex: 1 1 300px;
}
</style>

<style scoped>
input:active.disabled,
input:focus.disabled,
input:hover.disabled,
input.disabled {
    @apply bg-gray-300;
}
</style>
